import { Server } from 'lucide-react';
import { StatPill, type StatPillTone } from '../ui/StatPill';
import { cn } from '../../lib/utils';
import { cpuLabel } from '../../lib/cpu';
import type { NodeInfo, TaskInfo } from '../../lib/types';

export interface NodeListProps {
  nodes: NodeInfo[];
  tasks: TaskInfo[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}

function statusTone(status: NodeInfo['status']): StatPillTone {
  if (status === 'OK' || status === 'ready') return 'ok';
  if (status === 'down' || status === 'disconnected') return 'danger';
  return 'warn';
}

function statusLabel(status: NodeInfo['status']): string {
  if (status === 'OK' || status === 'ready') return 'OK';
  if (status === 'down') return 'DOWN';
  if (status === 'disconnected') return 'OFF';
  return 'DESC.';
}

function toneColor(v: number): string {
  return v > 80 ? '#e11d48' : v > 60 ? '#d97706' : '#16a34a';
}

function formatMemGiB(bytes: number): string {
  if (!bytes || bytes <= 0) return '—';
  const gib = bytes / 1024 ** 3;
  return `${gib >= 100 ? Math.round(gib) : gib.toFixed(1)} GiB`;
}

function workerLabel(n: NodeInfo): string | undefined {
  return n.labels?.label || undefined;
}

const NODE_GRID =
  'grid grid-cols-[24px_150px_76px_104px_minmax(0,1fr)_68px_40px_56px_56px_56px_52px_52px] items-center gap-x-3';

const HEADERS = [
  '',
  'Node',
  'Label',
  'IP',
  'Processador',
  'RAM',
  'Serv',
  'CPU',
  'MEM',
  'HD',
  'MÁQ',
  'Status',
];

const RIGHT_FROM = 5;

function Pct({ value, testId }: { value: number; testId: string }) {
  return (
    <span
      data-testid={testId}
      className="text-right font-semibold"
      style={{ color: toneColor(value) }}
    >
      {value}%
    </span>
  );
}

export function NodeList({
  nodes,
  tasks,
  selectedId,
  onSelect,
}: NodeListProps) {
  const tasksByNode = new Map<string, number>();
  tasks.forEach(t => {
    tasksByNode.set(t.nodeID, (tasksByNode.get(t.nodeID) ?? 0) + 1);
  });

  return (
    <div
      className="flex-1 overflow-auto bg-surface pb-1"
      data-testid="node-list-body"
    >
      <div
        data-testid="node-list-header"
        className={cn(
          NODE_GRID,
          'sticky top-0 z-10 mx-2 border border-transparent bg-surface px-3 pb-1 pt-2 text-[10px] font-bold uppercase tracking-[.06em] text-textMute'
        )}
      >
        {HEADERS.map((h, i) => (
          <span key={i} className={i >= RIGHT_FROM ? 'text-right' : undefined}>
            {h}
          </span>
        ))}
      </div>
      {nodes.length === 0 && (
        <div className="px-4 py-6 text-center text-[11.5px] text-textMute">
          Nenhum node disponível.
        </div>
      )}
      {nodes.map(n => {
        const isSel = n.id === selectedId;
        const servicosRodando = tasksByNode.get(n.id) ?? 0;
        const cpuPct = Math.max(0, Math.min(100, n.cpuUsedPct ?? 0));
        const memPct = Math.max(0, Math.min(100, n.memUsedPct ?? 0));
        const diskPct = Math.max(0, Math.min(100, n.diskUsedPct ?? 0));
        const label = workerLabel(n);
        const cpu = cpuLabel(n);
        return (
          <div
            key={n.id}
            data-testid={`node-row-${n.id}`}
            onClick={() => onSelect(n.id)}
            className={cn(
              'mx-2 my-1 cursor-pointer rounded-lg border px-3 py-1.5',
              'transition-[border-color,background] duration-150',
              isSel
                ? 'border-[var(--fm-accent)] bg-accentSoft shadow-[0_0_0_3px_rgba(59,130,246,.06)]'
                : 'border-border bg-surface hover:border-borderStrong hover:bg-bg'
            )}
          >
            <div
              data-testid={`node-line-${n.id}`}
              className={cn(NODE_GRID, 'font-mono text-[10.5px] tabular-nums')}
            >
              <div
                className={cn(
                  'inline-flex h-6 w-6 items-center justify-center rounded',
                  isSel
                    ? 'bg-[var(--fm-accent)] text-white'
                    : 'bg-surfaceAlt text-textMute'
                )}
              >
                <Server size={12} />
              </div>
              <span className="truncate text-[11.5px] font-bold text-text">
                {n.hostname}
              </span>
              <span className="min-w-0">
                {label && (
                  <span
                    data-testid={`node-label-${n.id}`}
                    title="Label do worker"
                    className="inline-block max-w-full truncate rounded bg-accentSoft px-1.5 py-px align-middle text-[9.5px] font-bold text-[var(--fm-accent)]"
                  >
                    {label}
                  </span>
                )}
              </span>
              <span
                data-testid={`node-ip-${n.id}`}
                className="truncate text-textMute"
              >
                {n.ip || '—'}
              </span>
              <span
                data-testid={`node-cpu-${n.id}`}
                className="truncate text-textMute"
                title={n.cpuModel}
              >
                {cpu ?? '—'}
              </span>
              <span
                data-testid={`node-mem-${n.id}`}
                className="text-right text-textMute"
              >
                {formatMemGiB(n.memoryBytes)}
              </span>
              <span
                data-testid={`node-serv-${n.id}`}
                className="text-right text-textMute"
              >
                {servicosRodando}
              </span>
              <Pct value={cpuPct} testId={`node-cpupct-${n.id}`} />
              <Pct value={memPct} testId={`node-mempct-${n.id}`} />
              <Pct value={diskPct} testId={`node-hdpct-${n.id}`} />
              <span
                data-testid={`node-maq-${n.id}`}
                className="text-right font-semibold text-text"
              >
                {n.machines}/{Math.max(1, n.machineAmount)}
              </span>
              <span className="flex justify-end">
                <StatPill tone={statusTone(n.status)}>
                  {statusLabel(n.status)}
                </StatPill>
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

NodeList.displayName = 'NodeList';

export default NodeList;
